import React, { useEffect, useState } from 'react';
import {
    Card, Form, Input, InputNumber, Select, TreeSelect, Table, Button, Space, Typography, Alert, Row, Col,
    Segmented, Tag, message
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Text, Link } = Typography;

const ROLE_OPTIONS = [
    { label: '平台视角', value: 'platform' },
    { label: '企业视角', value: 'enterprise' },
];

// 投放 App：一级 = App 名称，二级 = 系统-区域
const APP_TREE = [
    {
        title: '牵心 Pro',
        value: '牵心 Pro',
        children: [
            { title: 'iOS-国内', value: '牵心 Pro|iOS-国内' },
            { title: 'iOS-海外', value: '牵心 Pro|iOS-海外' },
            { title: 'Android-国内', value: '牵心 Pro|Android-国内' },
            { title: 'Android-海外', value: '牵心 Pro|Android-海外' },
        ],
    },
    {
        title: '云视App',
        value: '云视App',
        children: [
            { title: 'iOS-国内', value: '云视App|iOS-国内' },
            { title: 'iOS-海外', value: '云视App|iOS-海外' },
            { title: 'Android-国内', value: '云视App|Android-国内' },
            { title: 'Android-海外', value: '云视App|Android-海外' },
        ],
    },
    {
        title: '慧眼App',
        value: '慧眼App',
        children: [
            { title: 'iOS-国内', value: '慧眼App|iOS-国内' },
            { title: 'iOS-海外', value: '慧眼App|iOS-海外' },
            { title: 'Android-国内', value: '慧眼App|Android-国内' },
            { title: 'Android-海外', value: '慧眼App|Android-海外' },
        ],
    },
    {
        title: '小鹤看家',
        value: '小鹤看家',
        children: [
            { title: 'iOS-国内', value: '小鹤看家|iOS-国内' },
            { title: 'iOS-海外', value: '小鹤看家|iOS-海外' },
            { title: 'Android-国内', value: '小鹤看家|Android-国内' },
            { title: 'Android-海外', value: '小鹤看家|Android-海外' },
        ],
    },
];

// App 归属企业（用于企业身份过滤可选 App）
const APP_OWNER = {
    '牵心 Pro': '平台',
    '云视App': '汇云科技',
    '慧眼App': '汇云科技',
    '小鹤看家': '小鹤看家',
};

// App + 系统-区域 已配置的收款信息（只读，来源 App 配置；平台收款无商户号）
// 商户号：按 App 展示该 App 已配置的商户号，只读展示
const APP_PAY_CONFIG = {
    '牵心 Pro|iOS-国内': { receipt: '平台收款', channels: ['Apple Pay'] },
    '牵心 Pro|iOS-海外': { receipt: '平台收款', channels: ['Apple Pay'] },
    '牵心 Pro|Android-国内': { receipt: '平台收款', channels: ['微信支付', '支付宝'] },
    '牵心 Pro|Android-海外': { receipt: '平台收款', channels: ['Google Pay'] },
    '云视App|iOS-国内': { receipt: '企业收款', channels: ['Apple Pay'], merchant: '云视科技 · Apple Pay（apple_yunshi_01）' },
    '云视App|iOS-海外': { receipt: '企业收款', channels: ['Apple Pay'], merchant: '云视科技 · PayPal（pp_yunshi_01）' },
    '云视App|Android-国内': { receipt: '企业收款', channels: ['微信支付'], merchant: '云视科技 · 微信支付（wx_yunshi_01）' },
    '云视App|Android-海外': { receipt: '企业收款', channels: ['Google Pay'], merchant: '云视科技 · Google Pay（gp_yunshi_01）' },
    '慧眼App|iOS-国内': { receipt: '企业收款', channels: ['Apple Pay'], merchant: '慧眼科技 · Apple Pay（apple_huiyan_01）' },
    '慧眼App|Android-国内': { receipt: '企业收款', channels: ['微信支付', '支付宝'], merchant: '慧眼科技 · 微信支付（wx_huiyan_01）' },
    '慧眼App|iOS-海外': { receipt: '企业收款', channels: ['Apple Pay'] },
    '慧眼App|Android-海外': { receipt: '企业收款', channels: ['Google Pay'] },
    '小鹤看家|iOS-国内': { receipt: '平台收款', channels: ['Apple Pay'] },
    '小鹤看家|iOS-海外': { receipt: '平台收款', channels: ['Apple Pay'] },
    '小鹤看家|Android-国内': { receipt: '平台收款', channels: ['微信支付'] },
    '小鹤看家|Android-海外': { receipt: '平台收款', channels: ['Google Pay'] },
};

// 所属流量池：平台身份二级树（按企业分组），企业身份一级列表（仅本企业）
const CURRENT_ENT = '汇云科技';
const PLATFORM_POOLS = [
    {
        label: '鹤梦云平台',
        options: [{ label: '麦联宝', value: '麦联宝' }],
    },
    {
        label: '汇云科技',
        options: [
            { label: '万物互联电信', value: '万物互联电信' },
            { label: '桔子智联', value: '桔子智联' },
        ],
    },
    {
        label: '小鹤看家',
        options: [{ label: '五兴科技', value: '五兴科技' }],
    },
    {
        label: '产研测试验证企业',
        options: [{ label: '天贝云客猫', value: '天贝云客猫' }],
    },
];
// 企业身份：只取本企业分组下的一级流量池列表
const ENTERPRISE_POOLS = PLATFORM_POOLS.find((g) => g.label === CURRENT_ENT)?.options ?? [];

const PackageEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [mockRole, setMockRole] = useState('platform');

    const isPlatform = mockRole === 'platform';
    const isEdit = Boolean(id);
    // 所选投放节点（App|系统-区域）
    const selectedAppNodes = Form.useWatch('apps', form) || [];
    const poolOptions = isPlatform ? PLATFORM_POOLS : ENTERPRISE_POOLS;
    // 投放 App 数据来源：企业身份=本企业 App；平台身份=全部 App
    const appTreeData = isPlatform
        ? APP_TREE
        : APP_TREE.filter((n) => APP_OWNER[n.value] === CURRENT_ENT);

    useEffect(() => {
        if (isEdit) {
            form.setFieldsValue({
                name: '季度畅享30GB',
                pool: '万物互联电信',
                quota: 30,
                priceCny: 79,
                priceUsd: 12.99,
                apps: ['云视App|iOS-国内', '云视App|Android-国内'],
            });
        }
    }, [isEdit, id, form]);

    const handleSave = () => {
        form.validateFields().then(() => {
            message.success(isEdit ? '保存成功' : '创建成功');
            navigate('/traffic/package');
        });
    };

    // 支付配置表格（只读）：每个已选「系统-区域」一行，App 名称纵向合并
    const validLeaves = new Set(appTreeData.flatMap((n) => n.children.map((c) => c.value)));
    const payTableData = selectedAppNodes.filter((leaf) => validLeaves.has(leaf)).map((leaf) => {
        const [appName, region] = String(leaf).split('|');
        const cfg = APP_PAY_CONFIG[leaf] || {};
        return {
            leaf,
            appName,
            region,
            channels: cfg.channels || [],
            merchant: cfg.merchant || '',
            receipt: cfg.receipt || '企业收款',
            appRowSpan: 0,
        };
    });
    payTableData.forEach((row, idx) => {
        if (idx === 0 || payTableData[idx - 1].appName !== row.appName) {
            row.appRowSpan = payTableData.filter((r) => r.appName === row.appName).length;
        }
    });

    const payColumns = [
        {
            title: 'App 名称', dataIndex: 'appName', key: 'appName', width: 160,
            onCell: (record) => ({ rowSpan: record.appRowSpan }),
        },
        { title: '系统区域', dataIndex: 'region', key: 'region', width: 140 },
        {
            title: '支付方式', dataIndex: 'channels', key: 'channels', width: 260,
            render: (channels) => (channels.length
                ? channels.map((c) => <Tag key={c}>{c}</Tag>)
                : <Text type="secondary">-</Text>),
        },
        {
            title: '商户号', key: 'merchant',
            render: (_, r) => {
                // 平台收款：只展示支付方式，商户号为空
                if (r.receipt === '平台收款') return <Text type="secondary">-</Text>;
                if (r.merchant) return <Text>{r.merchant}</Text>;
                return (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        暂无商户号配置，请前往 <Link onClick={() => navigate('/app/merchant-enterprise')}>《企业商户号》</Link> 添加
                    </Text>
                );
            },
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space size={16}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/traffic/package')}>返回</Button>
                    <Title level={4} style={{ margin: 0 }}>{isEdit ? '编辑流量套餐' : '创建流量套餐'}</Title>
                </Space>
                <Space size={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
                    <Segmented value={mockRole} onChange={setMockRole} options={ROLE_OPTIONS} />
                </Space>
            </div>

            <Alert
                type="info"
                showIcon
                closable
                message="流量额度单位固定为 GB；售价维护人民币与美元两个固定价，不做国家精细区域定价；有效期本期不做，套餐长期有效。支付方式与商户号来源 App 配置，此处只读。"
                style={{ marginBottom: 16, borderRadius: 8 }}
            />

            <Card bordered={false} style={{ borderRadius: 8 }}>
                <Form form={form} layout="vertical" style={{ maxWidth: 900 }}>
                    <Form.Item label="套餐名称" name="name" rules={[{ required: true, message: '请输入套餐名称' }]}>
                        <Input placeholder="请输入套餐名称" />
                    </Form.Item>
                    <Form.Item
                        label="所属流量池"
                        name="pool"
                        rules={[{ required: true, message: '请选择所属流量池' }]}
                    >
                        <Select placeholder="请选择所属流量池" options={poolOptions} showSearch optionFilterProp="label" />
                    </Form.Item>
                    <Form.Item label="套餐流量额度" name="quota" rules={[{ required: true, message: '请输入套餐流量额度' }]}>
                        <InputNumber
                            min={0}
                            precision={2}
                            style={{ width: 220 }}
                            addonAfter="GB"
                            placeholder="请输入流量额度"
                        />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="人民币售价" name="priceCny" rules={[{ required: true, message: '请输入人民币售价' }]}>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    addonBefore="¥"
                                    placeholder="请输入人民币售价"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="美元售价" name="priceUsd" rules={[{ required: true, message: '请输入美元售价' }]}>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    addonBefore="$"
                                    placeholder="请输入美元售价"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="投放 App" name="apps" rules={[{ required: true, message: '请选择投放 App' }]}>
                        <TreeSelect
                            treeData={appTreeData}
                            treeCheckable
                            treeDefaultExpandAll
                            showCheckedStrategy={TreeSelect.SHOW_CHILD}
                            showSearch
                            filterTreeNode={(inputValue, treeNode) => String(treeNode.value).split('|')[0].toLowerCase().includes(inputValue.toLowerCase())}
                            allowClear
                            style={{ width: '100%' }}
                            placeholder="请选择投放的 App 与系统-区域（可多选）"
                        />
                    </Form.Item>

                    <Form.Item label="支付方式 / 商户号（只读，来源 App 配置）" required style={{ marginBottom: 0 }}>
                        <div>
                            {selectedAppNodes.length === 0 ? (
                                <Text type="secondary">请先选择投放 App</Text>
                            ) : (
                                <Table
                                    rowKey="leaf"
                                    size="small"
                                    bordered
                                    columns={payColumns}
                                    dataSource={payTableData}
                                    pagination={false}
                                />
                            )}
                        </div>
                    </Form.Item>
                </Form>

                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Button onClick={() => navigate('/traffic/package')} style={{ marginRight: 8 }}>取消</Button>
                    <Button type="primary" onClick={handleSave}>{isEdit ? '保存' : '创建'}</Button>
                </div>
            </Card>
        </div>
    );
};

export default PackageEdit;
